import { getOrderGroupCategories, isShowIngredients } from '../../../../../modal/order/Order/helper'
import { INVOICE_NON_PRODUCT_TYPES } from '../../../../../modal/order/Order/constants'
import { encryptGiftCode } from '../../../../../../../helper/encryptGiftCode'
import { getNeededDateFormat } from '../../../../../../../helper'

const CANCELED_ITEM_STATUSES = ['CANCELED', 'PREPARED_TO_CANCEL']
const POST_PAYMENT_NON_PRODUCT_TYPES = [...INVOICE_NON_PRODUCT_TYPES, 'TIPS']
const NUMBER_LOCALES = { de: 'de-DE', es: 'es-ES' }

const findItemByType = (order, type) => order?.purchaseItems?.find(item => item?.type === type)

const isActiveItem = item => !CANCELED_ITEM_STATUSES.includes(item.status)

export const calcInvoiceItemTotal = item => {
	const itemPrice = (item.price != null && isActiveItem(item)) ? item.price : 0
	const subItemsTotal = item.type === 'MENU'
		? (item.items || []).reduce((sum, subItem) => sum + calcInvoiceItemTotal(subItem), 0)
		: (item.items || []).filter(isActiveItem).reduce((sum, topping) => sum + ((topping.price && topping.quantity) ? topping.price * topping.quantity : 0), 0)
	const unitTotal = itemPrice + Math.max(subItemsTotal, 0)
	return item.quantity != null ? Math.round((unitTotal * item.quantity + Number.EPSILON) * 100) / 100 : unitTotal
}

const getSkuName = item => (item.sku ? `#${item.sku} ` : '') + item.name

const formatUnitPrice = price => price.toFixed(2).replace('.', ',')

const getPricedName = item => getSkuName(item) + (item.quantity > 1 ? ` (${formatUnitPrice(item.price)})` : '')

const toDetail = (text, level = 0) => ({ text, level })

const getToppingDetails = (toppings, level) => (toppings || []).map(topping =>
	toDetail(`${topping.quantity >= 0 ? '+' : '-'} ${topping.quantity}x ${getPricedName(topping)}`, level))

const getProductDetails = (product, giftCouponDetails, level = 0) => [
	...(isShowIngredients(product) ? [toDetail(product.ingredients, level)] : []),
	...giftCouponDetails.map(text => toDetail(text, level)),
	...getToppingDetails(product.items, level),
	...(product.remark ? [toDetail(product.remark, level)] : []),
]

const buildRow = (key, item, name, total, details) => ({
	key,
	quantity: item.quantity,
	name,
	unitPrice: item.quantity ? total / item.quantity : total,
	total,
	details,
})

const buildGiftCouponRow = (product, expirationDate, key) =>
	buildRow(key, { quantity: 1 }, product.name, product.price, [encryptGiftCode(product.couponMetaData?.code), expirationDate].map(text => toDetail(text)))

const getMenuItemDetails = subItem => [
	toDetail(`${subItem.quantity}x ${getPricedName(subItem)}`),
	...getProductDetails(subItem, [], 1),
]

const buildMenuRow = (menu, key) =>
	buildRow(key, menu, getPricedName(menu), calcInvoiceItemTotal(menu), (menu.items || []).flatMap(getMenuItemDetails))

const buildProductRow = (product, giftCouponDetails, key) =>
	buildRow(key, product, getPricedName(product), calcInvoiceItemTotal(product), getProductDetails(product, giftCouponDetails))

export const getInvoiceProductRows = (order, isPostPayment) => {
	const nonProductTypes = isPostPayment ? POST_PAYMENT_NON_PRODUCT_TYPES : INVOICE_NON_PRODUCT_TYPES
	const products = order?.purchaseItems?.filter(item => !nonProductTypes.includes(item?.type))
	const isGiftCouponOrder = order?.purchaseItems?.[0]?.type === 'GIFT_COUPON'
	const expirationDate = getNeededDateFormat(order?.purchaseItems?.[0]?.couponMetaData?.expirationDate)

	return (getOrderGroupCategories(products) || [])
		.flatMap(group => group.products)
		.map((product, i) => {
			const key = `product-${i}`
			if (product.type === 'GIFT_COUPON') return buildGiftCouponRow(product, expirationDate, key)
			if (product.type === 'MENU') return buildMenuRow(product, key)
			const giftCouponDetails = isGiftCouponOrder ? [encryptGiftCode(product.couponMetaData?.code), expirationDate] : []
			return buildProductRow(product, giftCouponDetails, key)
		})
}

const buildServiceRow = (key, name, amount) => buildRow(key, { quantity: 1 }, name, amount, [])

export const getInvoiceServiceRows = (order, t, lng, isPostPayment) => {
	const label = key => t(`invoices.modal.invoiceDataModal.order.${key}`, lng)
	const deliveryCost = findItemByType(order, 'DELIVERY_COST')
	const platformFee = findItemByType(order, 'PLATFORM_FEE')
	const discount = findItemByType(order, 'DISCOUNT')
	const extraDiscount = findItemByType(order, 'EXTRA_DISCOUNT')
	const coupon = findItemByType(order, 'MARKETING_COUPON')
	const oneTimeCoupon = findItemByType(order, 'ONE_TIME_COUPON')
	const categoryDiscount = order?.totalCategoryDiscount || 0
	const discountAmount = Math.abs(discount?.price || 0) + categoryDiscount

	return [
		deliveryCost && buildServiceRow('delivery-cost', label('directions'), deliveryCost.price),
		platformFee && buildServiceRow('platform-fee', `UberEats ${label('payment_fee')}`, platformFee.price),
		(discount || categoryDiscount !== 0) && buildServiceRow('discount', label('discount'), -discountAmount),
		extraDiscount && extraDiscount.price !== 0 && buildServiceRow('extra-discount', extraDiscount.name, -Math.abs(extraDiscount.price)),
		coupon && buildServiceRow('coupon', `${label('coupon')} ${coupon.name}`, coupon.price),
		oneTimeCoupon && buildServiceRow('one-time-coupon', `${label('oneTimeCoupon')} ${oneTimeCoupon.name}`, oneTimeCoupon.price),
		!isPostPayment && order?.tips > 0 && buildServiceRow('tips', label('tips'), order.tips),
		order?.paymentFee > 0 && buildServiceRow('payment-fee', label('payment_fee'), order.paymentFee),
	].filter(Boolean)
}

export const getInvoiceRows = (order, t, lng, isPostPayment) => [...getInvoiceProductRows(order, isPostPayment), ...getInvoiceServiceRows(order, t, lng, isPostPayment)]

export const getInvoiceSheetLanguage = company => company?.countryCode === 'ESP' ? 'es' : 'de'

export const getNumberLocale = language => NUMBER_LOCALES[language] || NUMBER_LOCALES.de

const berlinDateFormat = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric' })

export const formatBerlinDate = date => {
	if (!date) return
	const parts = Object.fromEntries(berlinDateFormat.formatToParts(new Date(date)).map(part => [part.type, part.value]))
	return `${parts.day}.${parts.month}.${parts.year}`
}

export const sumTips = items => (items || [])
	.filter(item => item?.type === 'TIPS')
	.reduce((sum, item) => sum + item.price * (item.quantity ?? 1), 0)

export const getVatRows = order => (order?.taxTotal?.taxes || []).map(tax => ({ title: tax.title, vat: tax.vat }))

export const getPostPaymentVatRows = order => (order?.taxTotal?.totalTaxes || [])
	.filter(tax => tax.taxType !== 'TAX_FREE')
	.map(tax => ({ title: `${tax.taxRate.toFixed(1)} %`, vat: tax.vat }))

export const getGrossTotals = order => (order?.payments || []).map(payment => payment.total)

export const getPostPaymentGrossTotal = order => (order?.payments || []).reduce((sum, payment) => sum + (payment?.total ?? 0), 0)

export const getPostPaymentNetTotal = (grossTotal, vatRows, tips) => grossTotal - vatRows.reduce((sum, row) => sum + row.vat, 0) - tips
